// utils/cacheManager.js

export const cacheManager = {
    /**
     * Sauvegarde une donnée dans localStorage avec expiration
     * @param {string} key
     * @param {any} data
     * @param {number} ttl Durée de validité en ms (par défaut 5 minutes)
     */
    set(key, data, ttl = 5 * 60 * 1000) {
        const record = {
            data,
            expiry: Date.now() + ttl,
        };
        localStorage.setItem(key, JSON.stringify(record));
    },

    /**
     * Récupère une donnée depuis localStorage si elle est valide
     * @param {string} key
     * @returns {any|null}
     */
    get(key) {
        const record = JSON.parse(localStorage.getItem(key));
        if (!record) return null;

        if (Date.now() > record.expiry) {
            localStorage.removeItem(key);
            return null;
        }
        return record.data;
    },

    /**
     * Supprime une entrée du cache
     * @param {string} key
     */
    remove(key) {
        localStorage.removeItem(key);
    },


    /**
     * Vide tout le cache
     */
    clear() {
        localStorage.clear();
    },


    /**
     * Stale-While-Revalidate
     * @param {string} key Nom du cache
     * @param {Function} fetcher Fonction pour récupérer les données depuis l’API
     * @param {number} ttl Durée de validité en ms
     * @returns {Promise<{data:any, fromCache:boolean}>}
     */
    async swr(key, fetcher, ttl = 5 * 60 * 1000) {
        const cached = this.get(key);

        // ⚡ Retourne le cache immédiatement si disponible
        if (cached) {
            // Lancer le refresh en arrière-plan
            fetcher().then((freshData) => {
                this.set(key, freshData, ttl);
            }).catch(() => {
                console.warn(`SWR: échec du refresh de ${key}`);
            });

            return { data: cached, fromCache: true };
        }

        // Sinon, fetch direct (pas de cache dispo)
        const freshData = await fetcher();
        this.set(key, freshData, ttl);
        return { data: freshData, fromCache: false };
    },

    /**
     * Vérifie si un state contient déjà des données,
     * sinon fait un appel API GET sur endpoint.
     * Si refresh = true, relance toujours la requête API.
     *
     * @param {string} endpoint - URL de l’API
     * @param {any} stateData - Données du state (tableau, objet, string, etc.)
     * @param {boolean} [refresh=false] - Force la récupération depuis l’API
     * @returns {Promise<{ data: any, fromState: boolean }>} Données existantes ou fraîches
     */
    async fetch(endpoint, stateData, refresh = false) {
        const hasData =
            (Array.isArray(stateData) && stateData.length > 0) ||
            (stateData && typeof stateData === "object" && Object.keys(stateData).length > 0) ||
            (typeof stateData === "string" && stateData.trim() !== "")

        if (hasData && !refresh) {
            return Promise.resolve({ data: stateData, fromState: true })
        }

        try {
            const response = await window.axios.get(endpoint)
            return { data: response.data, fromState: false }
        } catch (error) {
            return Promise.reject(error)
        }
    }


};
